import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { conversationsService } from 'api/services/conversations';
import {
  useActionApproval,
  useIsTablet,
  createWelcomeMessage,
  useResizable,
  useGigabotStreaming,
} from './hooks';
import {
  GigabotHeader,
  ConversationHistory,
  ChatMessages,
  ChatInput,
  MobileFAB,
  ErrorBoundary,
  ConfirmDialog,
} from './components';
import { PAGINATION, LIMITS, TIMING } from './utils/constants';
import './Gigabot.scss';

/**
 * Reusable Gigabot Chat Component
 *
 * Props:
 * - isOpen: boolean - Controls visibility of the chat panel
 * - onClose: function - Callback when chat is closed
 * - onOpen: function - Callback when chat is opened (for mobile FAB)
 * - placeholder: string - Input placeholder text
 * - containerElement: string - HTML element type ('div', 'aside', etc.)
 * - ariaProps: object - Additional ARIA properties
 * - initialMessage: object - Initial welcome message (optional)
 * - className: string - Additional CSS classes
 * - style: object - Inline styles for textarea (optional)
 * - prefilledContent: string - Content to prefill in the input (optional)
 * - context: string - Page context for tailored AI responses (optional)
 * - contextData: object - Context-specific data to send with chat messages (optional)
 * - environment: string - Conversation environment for scoping (profile, prompts, build, contracts, admin, global)
 * - showMobileFAB: boolean - Whether to show the mobile floating action button
 * - sessionId: string - External session ID to use (optional, generates new if not provided)
 * - onActionRun: function - Callback when action proposal is run (optional)
 * - onActionSkip: function - Callback when action proposal is skipped (optional)
 */
export const Gigabot = ({
  isOpen = false,
  onClose = () => {},
  onOpen = () => {},
  placeholder = 'Ask me anything...',
  containerElement = 'div',
  ariaProps = {},
  initialMessage = null,
  className = '',
  style = {},
  prefilledContent = '',
  prefilledTrigger = 0,
  onMessage = null,
  onUserInput = null,
  context = null,
  contextData = null,
  environment = 'global',
  showMobileFAB = true,
  resizable = true,
  minWidth = 400,
  maxWidth = 800,
  defaultWidth = 480,
  onResize = null,
  sessionId = null,
  onActionRun = null,
  onActionSkip = null,
}) => {
  // =====================================================
  // HOOKS
  // =====================================================

  // Redux state
  const { availableModels, enabledFeatures } = useSelector(
    (state) => state.global
  );
  const { action_proposals, web_search } = enabledFeatures || {};

  // Action approval hook
  const { addProposal, runProposal, skipProposal, getProposal } =
    useActionApproval({
      onRun: async (proposal) => {
        if (onActionRun) {
          await onActionRun(proposal);
        }
      },
      onSkip: (proposal) => {
        if (onActionSkip) {
          onActionSkip(proposal);
        }
      },
      autoCleanupDelay: 0,
    });

  const isTabletOrMobile = useIsTablet();

  const { panelWidth, isResizing, handleResizeStart } = useResizable({
    enabled: resizable,
    minWidth,
    maxWidth,
    defaultWidth,
    onResize,
  });

  // =====================================================
  // UI STATE (not handled by streaming hook)
  // =====================================================

  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [showAutoRunSettings, setShowAutoRunSettings] = useState(false);
  const [autoRunMode, setAutoRunMode] = useState('off');
  const [activeDropdown, setActiveDropdown] = useState(null);

  // History panel state
  const [showHistory, setShowHistory] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    conversation: null,
  });

  // Pagination state for messages
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  // =====================================================
  // STREAMING HOOK (core chat functionality)
  // =====================================================

  const {
    messages: gbMessages,
    input: gbInput,
    isLoading: gbLoading,
    activeConversationId,
    sessionId: gbSessionId,
    inputRef: gbInputRef,
    setInput: setGbInput,
    setSessionId: setGbSessionId,
    setActiveConversationId,
    setMessages: setGbMessages,
    sendMessage: handleGbSend,
    deleteMessage: handleDeleteMessage,
    resetMessages,
    moveToNextBlank,
  } = useGigabotStreaming({
    sessionId,
    context,
    contextData,
    selectedModel,
    webSearchEnabled,
    environment,
    onMessage,
    onUserInput,
    addProposal,
    initialMessage,
    prefilledContent,
  });

  // =====================================================
  // REFS (UI-specific)
  // =====================================================

  const gbMessagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const historyPanelRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // =====================================================
  // UTILITY FUNCTIONS
  // =====================================================

  const formatTime = useCallback((ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatRelativeTime = useCallback((date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  }, []);

  const resizeTextarea = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height =
        Math.min(textarea.scrollHeight, LIMITS.MAX_TEXTAREA_HEIGHT) + 'px';
    }
  }, []);

  // =====================================================
  // MESSAGE HANDLERS
  // =====================================================

  const handleMessageButtonClick = useCallback(
    (button) => {
      if (button.action && button.value) {
        setGbInput(button.value);
        setTimeout(() => gbInputRef.current?.focus(), 0);
      }
    },
    [setGbInput, gbInputRef]
  );

  const handleGbKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleGbSend();
      }
    },
    [handleGbSend]
  );

  const handleGbInputChange = useCallback(
    (e) => {
      setGbInput(e.target.value);
      resizeTextarea(e.target);
    },
    [setGbInput, resizeTextarea]
  );

  // Wrapper for delete that also closes dropdown
  const handleDeleteWithDropdownClose = useCallback(
    (messageId) => {
      handleDeleteMessage(messageId);
      setActiveDropdown(null);
    },
    [handleDeleteMessage]
  );

  // =====================================================
  // DROPDOWN & HISTORY HANDLERS
  // =====================================================

  const toggleDropdown = useCallback((messageId) => {
    setActiveDropdown((prev) => (prev === messageId ? null : messageId));
  }, []);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveDropdown(null);
    }
    // Don't close history if clicking the toggle button
    const isHistoryToggle = event.target.closest('.history-toggle-btn');
    if (
      historyPanelRef.current &&
      !historyPanelRef.current.contains(event.target) &&
      !isHistoryToggle
    ) {
      setShowHistory(false);
    }
  }, []);

  const fetchConversationHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const result = await conversationsService.getConversations({
        environment: environment,
        perPage: 20,
        page: 1,
      });
      if (result.success && result.data?.conversations) {
        setConversationHistory(result.data.conversations);
      }
    } catch (error) {
      console.error('Failed to fetch conversation history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [environment]);

  const loadConversation = useCallback(async (conversation) => {
    setHistoryLoading(true);
    try {
      const result = await conversationsService.getConversation(
        conversation.conversation_id,
        {
          includeMessages: true,
          messageLimit: PAGINATION.MAX_MESSAGES_PER_PAGE,
        }
      );
      if (result.success && result.data) {
        setActiveConversationId(conversation.conversation_id);
        const loadedMessages = (result.messages || []).map((msg, idx) => ({
          id: msg.id || idx,
          type: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content,
          timestamp: new Date(msg.created_at),
          isStreaming: false,
        }));
        if (loadedMessages.length === 0) {
          loadedMessages.push(createWelcomeMessage());
        }
        setGbMessages(loadedMessages);
        setHasMoreMessages(
          result.data.message_count > PAGINATION.MAX_MESSAGES_PER_PAGE
        );
        setMessagesPage(1);
        setShowHistory(false);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!activeConversationId || loadingMoreMessages || !hasMoreMessages)
      return;

    setLoadingMoreMessages(true);
    try {
      const nextPage = messagesPage + 1;
      const result = await conversationsService.getMessages(
        activeConversationId,
        {
          page: nextPage,
          perPage: PAGINATION.MAX_MESSAGES_PER_PAGE,
        }
      );
      if (result.success && result.data?.messages) {
        const olderMessages = result.data.messages.map((msg, idx) => ({
          id: msg.id || `old-${nextPage}-${idx}`,
          type: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content,
          timestamp: new Date(msg.created_at),
          isStreaming: false,
        }));
        setGbMessages((prev) => [...olderMessages, ...prev]);
        setMessagesPage(nextPage);
        setHasMoreMessages(result.data.has_next);
      }
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [
    activeConversationId,
    loadingMoreMessages,
    hasMoreMessages,
    messagesPage,
  ]);

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setGbMessages([createWelcomeMessage()]);
    setHasMoreMessages(false);
    setMessagesPage(1);
    setShowHistory(false);
    setTimeout(() => gbInputRef.current?.focus(), 100);
  }, []);

  const showDeleteConfirm = useCallback((conversation) => {
    setConfirmDialog({ isOpen: true, conversation });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const conversation = confirmDialog.conversation;
    if (!conversation) return;

    setConfirmDialog({ isOpen: false, conversation: null });

    try {
      const result = await conversationsService.deleteConversation(
        conversation.conversation_id
      );
      if (result.success) {
        // Remove from local state
        setConversationHistory((prev) =>
          prev.filter((c) => c.conversation_id !== conversation.conversation_id)
        );
        // If this was the active conversation, start a new one
        if (activeConversationId === conversation.conversation_id) {
          startNewConversation();
        }
      } else {
        console.error('Failed to delete conversation:', result.error);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [confirmDialog.conversation, activeConversationId, startNewConversation]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDialog({ isOpen: false, conversation: null });
  }, []);

  const handleAutoRunModeChange = useCallback((mode) => {
    setAutoRunMode(mode);
    setShowAutoRunSettings(false);
  }, []);

  // =====================================================
  // EFFECTS
  // =====================================================

  // Auto-scroll to latest message
  useEffect(() => {
    requestAnimationFrame(() => {
      gbMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [gbMessages]);

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isOpen && gbInputRef.current) {
      gbInputRef.current.focus();
    }
  }, [isOpen]);

  // Manage body scroll on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('gigabot-mobile-open');
    } else {
      document.body.classList.remove('gigabot-mobile-open');
    }
    return () => {
      document.body.classList.remove('gigabot-mobile-open');
    };
  }, [isOpen]);

  // Update input when prefilledContent changes
  useEffect(() => {
    if (prefilledContent) {
      setGbInput(prefilledContent);
    }
  }, [prefilledContent, prefilledTrigger]);

  // Sync sessionId prop with internal state when it changes
  useEffect(() => {
    if (sessionId && sessionId !== gbSessionId) {
      setGbSessionId(sessionId);
    }
    // Note: gbSessionId intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Update initial message when it changes
  useEffect(() => {
    if (initialMessage) {
      setGbMessages((prev) => {
        if (prev.length > 0 && prev[0].id === initialMessage.id) {
          return [initialMessage];
        }
        return prev;
      });
    }
  }, [initialMessage]);

  // Handle clicking outside dropdown
  useEffect(() => {
    if (activeDropdown || showHistory) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeDropdown, showHistory, handleClickOutside]);

  // Fetch conversation history when panel opens
  useEffect(() => {
    if (showHistory) {
      fetchConversationHistory();
    }
  }, [showHistory, fetchConversationHistory]);

  // Auto-resize textarea
  useEffect(() => {
    resizeTextarea(gbInputRef.current);
  }, [gbInput, resizeTextarea]);

  // Set default model
  useEffect(() => {
    if (availableModels.length > 0 && !selectedModel) {
      setSelectedModel(availableModels[0].id);
    }
  }, [availableModels, selectedModel]);

  // Infinite scroll for loading more messages (debounced)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !hasMoreMessages) return;

    let timeoutId = null;
    const handleScroll = () => {
      // Debounce scroll handler to prevent excessive calls
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (
          container.scrollTop < LIMITS.SCROLL_THRESHOLD &&
          !loadingMoreMessages
        ) {
          loadMoreMessages();
        }
      }, TIMING.SCROLL_DEBOUNCE);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreMessages, loadingMoreMessages, loadMoreMessages]);

  // =====================================================
  // RENDER
  // =====================================================

  const ContainerElement = containerElement;

  const defaultAriaProps = {
    role: 'dialog',
    'aria-modal': 'false',
    'aria-label': 'Gigabot chat panel',
    'aria-hidden': !isOpen,
  };

  const mergedAriaProps = { ...defaultAriaProps, ...ariaProps };

  const panelStyle =
    resizable && !isTabletOrMobile
      ? {
          width: `${panelWidth}px`,
          maxWidth: maxWidth,
          minWidth: minWidth,
          transition: isResizing ? 'none' : 'width 220ms ease',
        }
      : {};

  return (
    <>
      <MobileFAB isVisible={showMobileFAB && !isOpen} onClick={onOpen} />

      <ContainerElement
        className={`gigabot-chat ${isOpen ? 'open' : ''} ${
          resizable ? 'resizable' : ''
        } ${isResizing ? 'resizing' : ''} ${className}`}
        style={panelStyle}
        {...mergedAriaProps}
      >
        {/* Resize Handle */}
        {resizable && (
          <div
            className='gigabot-resize-handle'
            onMouseDown={handleResizeStart}
            aria-label='Resize panel'
            role='separator'
            aria-orientation='vertical'
          />
        )}

        <GigabotHeader
          onClose={onClose}
          showHistory={showHistory}
          onToggleHistory={() => setShowHistory(!showHistory)}
          showActionProposals={action_proposals}
          autoRunMode={autoRunMode}
          onAutoRunModeChange={handleAutoRunModeChange}
          showAutoRunSettings={showAutoRunSettings}
          onToggleAutoRunSettings={() =>
            setShowAutoRunSettings(!showAutoRunSettings)
          }
          hasActiveConversation={!!activeConversationId}
          onNewConversation={startNewConversation}
        />

        <ConversationHistory
          ref={historyPanelRef}
          isOpen={showHistory}
          conversations={conversationHistory}
          activeConversationId={activeConversationId}
          isLoading={historyLoading}
          onSelectConversation={loadConversation}
          onDeleteConversation={showDeleteConfirm}
          onNewConversation={startNewConversation}
          formatRelativeTime={formatRelativeTime}
        />

        <div ref={messagesContainerRef} className='gigabot-messages-container'>
          {loadingMoreMessages && (
            <div className='loading-more-messages'>
              <div className='loading-spinner small'></div>
            </div>
          )}
          <ErrorBoundary
            errorMessage='Something went wrong displaying the chat messages.'
            showRetry
          >
            <ChatMessages
              ref={gbMessagesEndRef}
              messages={gbMessages}
              formatTime={formatTime}
              activeDropdown={activeDropdown}
              onToggleDropdown={toggleDropdown}
              onDeleteMessage={handleDeleteWithDropdownClose}
              onButtonClick={handleMessageButtonClick}
              dropdownRef={dropdownRef}
              showActionProposals={action_proposals}
              autoRunMode={autoRunMode}
              onActionRun={runProposal}
              onActionSkip={skipProposal}
              getProposal={getProposal}
            />
          </ErrorBoundary>
        </div>

        <ChatInput
          ref={gbInputRef}
          value={gbInput}
          onChange={handleGbInputChange}
          onSubmit={handleGbSend}
          onKeyDown={handleGbKeyDown}
          placeholder={placeholder}
          disabled={gbLoading}
          isLoading={gbLoading}
          style={style}
          availableModels={availableModels}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          webSearchEnabled={webSearchEnabled}
          onWebSearchChange={setWebSearchEnabled}
          showWebSearch={web_search}
        />
      </ContainerElement>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title='Delete Conversation'
        message='Are you sure you want to delete this conversation? This cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        variant='danger'
      />
    </>
  );
};

Gigabot.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  placeholder: PropTypes.string,
  containerElement: PropTypes.string,
  ariaProps: PropTypes.object,
  initialMessage: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    content: PropTypes.string,
    timestamp: PropTypes.instanceOf(Date),
  }),
  className: PropTypes.string,
  style: PropTypes.object,
  prefilledContent: PropTypes.string,
  prefilledTrigger: PropTypes.number,
  onMessage: PropTypes.func,
  onUserInput: PropTypes.func,
  context: PropTypes.string,
  contextData: PropTypes.object,
  environment: PropTypes.oneOf([
    'profile',
    'prompts',
    'build',
    'contracts',
    'admin',
    'global',
  ]),
  showMobileFAB: PropTypes.bool,
  resizable: PropTypes.bool,
  minWidth: PropTypes.number,
  maxWidth: PropTypes.number,
  defaultWidth: PropTypes.number,
  onResize: PropTypes.func,
  sessionId: PropTypes.string,
  onActionRun: PropTypes.func,
  onActionSkip: PropTypes.func,
};

export default Gigabot;
